import { GarageRole } from '../interface/garage';
import { NavChild } from '../interface/nav';
import { global } from '../config/app.constants';
import { ROLE } from '@parkingless/interface';

/**
 * The sidebar menu for the one garage currently selected.
 *
 * The console used to render every garage the user could act on as its own collapsible
 * group, all of them in the sidebar at once. That put the selection in the same place as
 * the navigation, so which garage you were working in was something you inferred from
 * which group happened to be open. Selection now lives in the header, and the sidebar
 * shows the pages of the selected garage and nothing else — which also matches how the
 * data actually works, since every screen queries one garage at a time.
 *
 * Employees get a deliberately shorter menu than owners and admins, which is why this
 * takes the whole role entry rather than just an id.
 *
 * Pure, so the menu a given role sees is testable without rendering anything.
 */
export function buildGarageMenu(entry: GarageRole | null | undefined): NavChild[] {
  const garageId = entry?.garage?.garageId;
  if (!entry || !garageId) {
    return [];
  }

  const pages = entry.role === ROLE.employee ? global.garageEmployeeSubPage : global.garageSubPage;

  return pages.map((page) => ({
    path: `/parking/${garageId}/${page.path}`,
    title: page.title,
    abbreviation: page.title.slice(0, 2),
    icon: page.ab,
  }));
}

/**
 * The landing page for a garage, for when a destination is needed but no particular
 * screen is: switching garage from a route that has no equivalent, or picking one from
 * the header while looking at the garage list.
 *
 * An employee has no dashboard — `employeeGuard` bounces them off it — so this is the
 * first page their own menu actually contains rather than a fixed route.
 */
export function garageLandingPath(entry: GarageRole): string {
  const pages = entry.role === ROLE.employee ? global.garageEmployeeSubPage : global.garageSubPage;

  return `/parking/${entry.garage.garageId}/${pages[0].path}`;
}
